"use client";

import {usePostHog} from "@posthog/next";
import {useCallback, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useAlert} from "@/hooks/useAlert";
import {
   type DictationRecorderError,
   type DictationStopReason,
   useDictationRecorder,
} from "@/hooks/useDictationRecorder";
import {
   appendDictationTranscript,
   dictationErrorMessage,
   reportDictationUsage,
} from "@/utils/chat/dictation";

export type {GailGptDictationStatus} from "@/hooks/useDictationRecorder";

type UseGailGptDictationProps = {
   value: string;
   onChange: (value: string) => void;
   disabled?: boolean;
   onTranscriptInserted?: () => void;
};

function autoStopMessageKey(reason: Exclude<DictationStopReason, "user">): string {
   switch (reason) {
      case "max_duration":
         return "dictation.stoppedMaxDuration";
      case "tab_hidden":
         return "dictation.stoppedTabHidden";
      default: {
         const _exhaustive: never = reason;
         return _exhaustive;
      }
   }
}

function failureMessage(
   t: (key: string) => string,
   {reason, status}: DictationRecorderError
): string {
   if (reason === "empty_audio" || reason === "empty_transcript") {
      return t("dictation.empty");
   }
   if (reason === "mic_denied") {
      return t("dictation.micDenied");
   }
   return dictationErrorMessage(t, reason, status ?? 0);
}

/**
 * Click-to-record, click-to-stop Soniox dictation. Live tokens appear in the
 * composer while recording; the user still sends the message themselves.
 */
export function useGailGptDictation({
   value,
   onChange,
   disabled = false,
   onTranscriptInserted,
}: UseGailGptDictationProps) {
   const {t} = useTranslation("ask");
   const {setAlert} = useAlert();
   const posthog = usePostHog();

   const valueRef = useRef(value);
   const baseValueRef = useRef(value);
   const onChangeRef = useRef(onChange);
   const onTranscriptInsertedRef = useRef(onTranscriptInserted);

   useEffect(() => {
      valueRef.current = value;
   }, [value]);

   useEffect(() => {
      onChangeRef.current = onChange;
   }, [onChange]);

   useEffect(() => {
      onTranscriptInsertedRef.current = onTranscriptInserted;
   }, [onTranscriptInserted]);

   const showError = useCallback(
      (
         message: string,
         reason: string,
         extras: {durationMs?: number; producedText?: boolean} = {}
      ) => {
         setAlert({
            status: "error",
            message,
            secondsToClose: 10,
         });
         posthog?.capture("gailgpt_dictation_failed", {
            reason,
            duration_ms: extras.durationMs ?? 0,
            produced_text: extras.producedText ?? false,
         });
      },
      [posthog, setAlert]
   );

   const applyTranscript = useCallback((baseValue: string, transcript: string) => {
      const nextValue = appendDictationTranscript(baseValue, transcript);
      const changed = nextValue !== valueRef.current;
      valueRef.current = nextValue;
      if (changed) {
         onChangeRef.current(nextValue);
      }
      return nextValue !== baseValue;
   }, []);

   const {status, isSupported, toggle: toggleRecording, cancel} = useDictationRecorder({
      disabled,
      onStarted: () => {
         baseValueRef.current = valueRef.current;
         posthog?.capture("gailgpt_dictation_started", {});
      },
      onError: (error) => {
         showError(failureMessage(t, error), error.reason, {
            durationMs: error.durationMs,
            producedText: error.producedText,
         });
      },
      onAutoStop: (reason) => {
         setAlert({
            status: "info",
            message: t(autoStopMessageKey(reason)),
            secondsToClose: 10,
         });
      },
      onLiveTranscript: (text) => {
         applyTranscript(baseValueRef.current, text);
      },
      onTranscript: (text, {durationMs, stopReason}) => {
         const inserted = applyTranscript(baseValueRef.current, text);
         if (!inserted) {
            showError(t("dictation.empty"), "empty_transcript");
            return;
         }
         onTranscriptInsertedRef.current?.();
         const trimmed = text.trim();
         const characters = trimmed.length;
         void reportDictationUsage({durationMs, characters});
         posthog?.capture("gailgpt_dictation_completed", {
            duration_ms: durationMs,
            produced_text: true,
            text_length: characters,
            stop_reason: stopReason,
         });
      },
   });

   const toggle = useCallback(() => {
      if (disabled) {
         return;
      }
      if (status === "unsupported") {
         showError(t("dictation.unsupported"), "unsupported");
         return;
      }
      toggleRecording();
   }, [disabled, showError, status, t, toggleRecording]);

   return {
      status,
      isSupported,
      toggle,
      cancel,
   };
}
